/**
 * @coeldery/pwa-spa —— **Vite／SPA 用嘅 PWA 外殼**（manifest ＋ service worker 產生器）
 *
 * 為何有呢個模組（R1 查庫證據，SMS 2026-09-28 P1.5）：
 *   · 庫內只有 `pwa-head-html`（**明文 `notFor` SPA 嘅 `index.html`** → 唔適用）
 *   · `--search offline｜service-worker｜manifest` 全部 **0 命中**
 *   → 依 R3 新建（零依賴、純字串產生，唔識任何框架）
 *
 * ⭐ 快取策略（**SMS `decisions/015` §2.5 定；出帳類系統一定要守**）
 *   · **HTML（navigate）同 `/api/*` 一律 network-only** —— 有網就一定打真 server，
 *     唔會出現「部署咗新版本但用戶見到舊 HTML」或「攞住上次嘅庫存數去出帳」
 *   · 只有**帶 hash 嘅靜態資產**（`/assets/*`、icon）行 cache-first（檔名一變就係新檔，安全）
 *   · 完全離線 → 只回離線提示頁（一句「沒有網絡」＋ 重試），**唔會**回舊資料
 *
 * ⚠️ 本模組**唔包圖示**（icon 係品牌資產，由 app 自己出 PNG／SVG）。
 */

/** manifest 用嘅 icon 條目 */
export interface PwaIcon {
  src: string;
  sizes: string;
  type: string;
  /** `any`（一般）｜`maskable`（Android 自適應圖示） */
  purpose?: 'any' | 'maskable';
}

export interface PwaOptions {
  /** 完整名（例 `CoEldery 85 庫存管理`） */
  name: string;
  /** 捷徑名（≤12 字元；桌面圖示下面嘅字） */
  shortName: string;
  description?: string;
  /** 由 app 傳（**唔准模組寫死顏色**；SMS 用品牌色） */
  themeColor: string;
  backgroundColor: string;
  startUrl?: string;
  scope?: string;
  lang?: string;
  icons?: readonly PwaIcon[];
}

/** 砌 `manifest.webmanifest` 嘅內容（純物件；呼叫者自己 `JSON.stringify` 寫檔） */
export function buildManifest(o: PwaOptions): Record<string, unknown> {
  return {
    name: o.name,
    short_name: o.shortName,
    description: o.description ?? o.name,
    lang: o.lang ?? 'zh-HK',
    start_url: o.startUrl ?? '/',
    scope: o.scope ?? '/',
    display: 'standalone',
    orientation: 'portrait',
    theme_color: o.themeColor,
    background_color: o.backgroundColor,
    icons: [...(o.icons ?? [])],
  };
}

export interface SwOptions {
  /** 換版本 ＝ 換 cache 名（一定要同 build 一齊變，否則舊 cache 唔會清） */
  version: string;
  /**
 * 離線時回邊個檔（預設 `/offline`）。
 * ⚠️ **一定要用 clean URL**（`/offline` 而唔係 `/offline.html`）：Cloudflare Pages 會將
 *   `X.html` **308** 去 `/X`；`Cache.addAll()` 會跟隨重定向，令 cache 內嘅回應變成
 *   `redirected === true` → 服務 worker 之後唔可以用佢回應**導覽請求**（會掟錯）→ 離線頁會壞。
 *   （血證：2026-10-06 hksca 實測 `/offline.html` → 308、`/offline` → 200。）
 */
  offlineUrl?: string;
  /** 預先快取嘅**靜態**檔（⚠️ **唔准**放 HTML 或 `/api/*`） */
  precache?: readonly string[];
}

/**
 * 產生 service worker 源碼（寫落 `/sw.js`）。
 * ⚠️ 用字串砌而唔係 `.toString()` 一個函式：Vite 會把函式壓縮（改名／箭頭化），
 *    字串就一定可控、可審計（同 `@coeldery/table-io` 出 XML 嘅理由一樣）。
 */
export function serviceWorkerSource(o: SwOptions): string {
  const cache = `pwa-shell-${o.version}`;
  const offline = o.offlineUrl ?? '/offline';
  const precache = [...new Set([...(o.precache ?? []), offline])];
  return [
    `/* 由 @coeldery/pwa-spa 產生（version ${o.version}）—— 不得手動修改 */`,
    `const CACHE = ${JSON.stringify(cache)};`,
    `const OFFLINE = ${JSON.stringify(offline)};`,
    `const PRECACHE = ${JSON.stringify(precache)};`,
    '',
    'self.addEventListener("install", (e) => {',
    '  e.waitUntil((async () => {',
    '    const c = await caches.open(CACHE);',
    '    await c.addAll(PRECACHE);',
    '    await self.skipWaiting();',
    '  })());',
    '});',
    '',
    'self.addEventListener("activate", (e) => {',
    '  e.waitUntil((async () => {',
    '    for (const k of await caches.keys()) if (k !== CACHE) await caches.delete(k);',
    '    await self.clients.claim();',
    '  })());',
    '});',
    '',
    'self.addEventListener("fetch", (e) => {',
    '  const req = e.request;',
    '  if (req.method !== "GET") return;',
    '  const url = new URL(req.url);',
    '  if (url.origin !== self.location.origin) return;',
    '',
    '  // ⭐ HTML 及 API：NETWORK-ONLY（每次進入應用程式取最新；離線只回提示頁，不得輸出舊資料）',
    '  if (req.mode === "navigate" || url.pathname.indexOf("/api/") === 0) {',
    '    e.respondWith((async () => {',
    '      try {',
    '        return await fetch(req, { cache: "no-store" });',
    '      } catch (err) {',
    '        const hit = await caches.match(OFFLINE);',
    '        return hit || new Response("offline", { status: 503, headers: { "Content-Type": "text/plain" } });',
    '      }',
    '    })());',
    '    return;',
    '  }',
    '',
    '  // 帶 hash 的靜態資產：cache-first（檔名一變即為新檔，安全）',
    '  e.respondWith((async () => {',
    '    const hit = await caches.match(req);',
    '    if (hit) return hit;',
    '    const res = await fetch(req);',
    '    if (res && res.ok && res.type === "basic") {',
    '      const copy = res.clone();',
    '      const c = await caches.open(CACHE);',
    '      await c.put(req, copy);',
    '    }',
    '    return res;',
    '  })());',
    '});',
    '',
  ].join('\n');
}

const OFFLINE_TEXT: Record<string, { title: string; body: string; retry: string }> = {
  'zh-HK': { title: '沒有網絡', body: '請重新連接網絡後再試（本應用程式不會顯示舊資料，以免誤讀數據）。', retry: '重試' },
  en: { title: 'No connection', body: 'Please reconnect and retry (this app never shows stale figures).', retry: 'Retry' },
  'zh-CN': { title: '没有网络', body: '请重新连接网络后重试（本应用程式不会显示旧数据，以免误读数据）。', retry: '重试' },
};

/** 產生 `offline.html`（三語；用瀏覽器語言揀，唔靠 i18n runtime） */
export function offlineHtml(appName: string): string {
  const dict = JSON.stringify(OFFLINE_TEXT);
  return [
    '<!doctype html>',
    '<html lang="zh-HK">',
    '<head>',
    '  <meta charset="UTF-8" />',
    '  <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />',
    '  <title>Offline</title>',
    '</head>',
    '<body style="margin:0;font-family:system-ui,-apple-system,sans-serif">',
    '  <main style="min-height:100svh;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;padding:24px;text-align:center">',
    '    <h1 id="t" style="margin:0;font-size:26px"></h1>',
    '    <p id="b" style="margin:0;font-size:18px;color:#555"></p>',
    '    <button id="r" type="button" style="min-height:44px;padding:10px 18px;font-size:16px;border-radius:10px;border:1px solid #ccc;background:#fff"></button>',
    `    <p style="margin:0;font-size:15px;color:#888">${appName}</p>`,
    '  </main>',
    '  <script>',
    `    var D = ${dict};`,
    "    var lang = (navigator.language || 'zh-HK').toLowerCase();",
    "    var key = lang.indexOf('cn') >= 0 || lang.indexOf('hans') >= 0 ? 'zh-CN' : (lang.indexOf('en') === 0 ? 'en' : 'zh-HK');",
    '    document.documentElement.lang = key;',
    '    document.getElementById("t").textContent = D[key].title;',
    '    document.getElementById("b").textContent = D[key].body;',
    '    document.getElementById("r").textContent = D[key].retry;',
    '    document.getElementById("r").addEventListener("click", function () { location.reload(); });',
    '  </script>',
    '</body>',
    '</html>',
    '',
  ].join('\n');
}

/**
 * 註冊 service worker（**只喺呼叫者話 enabled 嘅時候**；通常 ＝ 生產 build）。
 * ⚠️ 唔好喺 dev server 註冊（會 cache 住舊 asset，令 HMR 鬼打牆）。
 */
export function registerServiceWorker(opts: { enabled: boolean; url?: string }): void {
  if (!opts.enabled) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const url = opts.url ?? '/sw.js';
  // 等頁面載完先註冊（唔好同首屏爭頻寬）
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register(url).catch(() => {
      /* 註冊失敗唔應該影響 app（PWA 只係加分項） */
    });
  });
}

/** 一次性檢查（除錯／測試用）：呢個環境裝唔裝得成 PWA。回 `{ ok, reasons[] }`（唔會 throw） */
export function pwaChecks(opts: { manifestUrl: string; swUrl: string; hasHttps: boolean }): {
  ok: boolean;
  reasons: string[];
} {
  const reasons: string[] = [];
  if (!opts.hasHttps) reasons.push('要 HTTPS（或 localhost）');
  if (!opts.manifestUrl) reasons.push('缺 manifest URL');
  if (!opts.swUrl) reasons.push('缺 service worker URL');
  return { ok: reasons.length === 0, reasons };
}

